// The respondent data view (#1887): the page the server sends, and the pure
// rules the page, the grid and "Send to agent" share. The route is
// GET /job/:customer/:job/casedata/view (goapi/pkg/server/casedata_view.go).

export type CaseDataType = 'single' | 'multi' | 'grid' | 'number'

export interface CaseDataColumn {
  name: string
  description: string
  data_type: CaseDataType
  /** code → label (a grid's top frame). */
  codes: Record<string, string>
  /** A grid's side frame. */
  side_codes?: Record<string, string>
}

/** A code, a list of codes (multi), [top, side] pairs (grid), a number, or missing. */
export type CaseDataValue = number | number[] | [number, number][] | null

export interface CaseDataRow {
  /** 1-based position in the data. */
  index: number
  /** The case variable's value, or the index when the job has none. */
  case: CaseDataValue
  values: CaseDataValue[]
}

export interface CaseDataViewPage {
  /** '' when the case column is the case index. */
  case_variable: string
  columns: CaseDataColumn[]
  rows: CaseDataRow[]
  total_cases: number
  filtered_cases: number
  offset: number
  limit: number
}

export interface CaseDataViewAccess {
  user_email: string
  /** Epoch ms. */
  at: number
  status_code: number
  varnames?: string
  filter?: string
  format?: string
}

export const CASE_DATA_PAGE_SIZES = [100, 200, 500] as const
export const CASE_DATA_DEFAULT_PAGE_SIZE = 200

/**
 * The view as the page's URL keeps it, so a reload or a shared link opens the
 * same view: the columns in order, the filter expression, the page, its size
 * and the codes switch.
 */
export interface CaseDataViewState {
  columns: string[]
  /**
   * The quick filter's expression in Carbon's syntax, e.g.
   * gender(1)&(region(1;3)|!age(*)); '' for none. goapi evaluates it
   * against the case data (casedata_filter.go).
   */
  filter: string
  offset: number
  limit: number
  showCodes: boolean
}

// cols=gender,brands  filter=region(1;3)&!age(*)  offset=200  rows=500  codes=1

const splitList = (s: string | undefined, sep: string) => (s ?? '').split(sep).map((p) => p.trim()).filter(Boolean)

/** Splits s at sep where it is outside brackets. */
function splitTopLevel(s: string, sep: string): string[] {
  const parts: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < s.length; i++) {
    const c = s[i]
    if (c === '(' || c === '{' || c === '[') depth++
    else if (c === ')' || c === '}' || c === ']') depth--
    else if (c === sep && depth === 0) {
      parts.push(s.slice(start, i))
      start = i + 1
    }
  }
  parts.push(s.slice(start))
  return parts
}

/** An old link's variable with nothing ticked: name(), which filtered nothing. */
const EMPTY_PART = /^[^()]+\(\s*\)$/

/**
 * The filter parameter as an expression. Before 20 Oct the view kept its
 * ticks as comma-separated var(codes), every one to hold, with var() for a
 * variable with nothing ticked: a shared link of that kind opens as the same
 * filter, its parts joined by & and the empty ones dropped. An expression
 * comes back as it is.
 */
export function migrateFilterParam(raw: string | undefined): string {
  const value = (raw ?? '').trim()
  const parts = splitTopLevel(value, ',').map((p) => p.trim()).filter(Boolean)
  if (parts.length <= 1 && !EMPTY_PART.test(value)) return value
  return parts
    .filter((p) => !EMPTY_PART.test(p))
    .map((p) => (splitTopLevel(p, '|').length > 1 ? `(${p})` : p))
    .join('&')
}

/**
 * The expression as the URL keeps it: trimmed, and v() written v(*). Both
 * mean "answered", and an old link's v() meant "nothing ticked", so the URL
 * never holds a new v().
 */
export function canonicalFilter(expr: string): string {
  return expr.trim().replace(/([^\s()&|!~,]+)\(\s*\)/g, '$1(*)')
}

/** Reads the view from the route's params; anything missing or wrong is its default. */
export function parseViewParams(params: Record<string, string | undefined>): CaseDataViewState {
  const offset = Number(params.offset)
  const limit = Number(params.rows)
  return {
    columns: splitList(params.cols, ','),
    filter: migrateFilterParam(params.filter),
    offset: Number.isInteger(offset) && offset > 0 ? offset : 0,
    limit: (CASE_DATA_PAGE_SIZES as readonly number[]).includes(limit) ? limit : CASE_DATA_DEFAULT_PAGE_SIZE,
    showCodes: params.codes === '1',
  }
}

/** The route params for a view; a default is left out. */
export function viewParams(view: CaseDataViewState): Record<string, string> {
  const params: Record<string, string> = {}
  if (view.columns.length > 0) params.cols = view.columns.join(',')
  if (view.filter) params.filter = view.filter
  if (view.offset > 0) params.offset = String(view.offset)
  if (view.limit !== CASE_DATA_DEFAULT_PAGE_SIZE) params.rows = String(view.limit)
  if (view.showCodes) params.codes = '1'
  return params
}

function labelOf(codes: Record<string, string> | undefined, code: number): string {
  return codes?.[String(code)] || String(code)
}

/** A cell as the grid shows it: labels by default, codes when asked; a number column is always its number. */
export function formatCaseValue(value: CaseDataValue, column: Pick<CaseDataColumn, 'data_type' | 'codes' | 'side_codes'>, showCodes: boolean): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') {
    if (column.data_type === 'number' || showCodes) return String(value)
    return labelOf(column.codes, value)
  }
  if (value.length === 0) return ''
  if (Array.isArray(value[0])) {
    return (value as [number, number][])
      .map(([top, side]) => showCodes ? `${top}:${side}` : `${labelOf(column.codes, top)}: ${labelOf(column.side_codes, side)}`)
      .join('; ')
  }
  return (value as number[]).map((code) => showCodes ? String(code) : labelOf(column.codes, code)).join('; ')
}

/** The case column's text: the value as it is, never a label. */
export function formatCaseId(value: CaseDataValue): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return String(value)
  return formatCaseValue(value, { data_type: 'single', codes: {} }, true)
}

const TYPE_NOTES: Partial<Record<CaseDataType, string>> = {
  // Nothing in a MET marks a multi-response question, so a page whose
  // answers hold one code each reads as single.
  single: 'coded: an answer is a code, or a list of codes where the question allows more than one',
  multi: 'multi-response: each answer is a list of codes',
  grid: 'grid: each answer is a list of top:side code pairs',
  number: 'a number, not a code',
}

/**
 * The message "Send to agent" puts in the start screen's composer: the job,
 * the variables, the filter expression (which `pt casedata --filter` reads
 * as it is), and how to read the rows. The person finishes it with their
 * question.
 */
export function buildAgentDraft(opts: {
  job: string
  columns: Pick<CaseDataColumn, 'name' | 'description' | 'data_type'>[]
  filter: string
  caseVariable: string
  filteredCases?: number
  totalCases?: number
}): string {
  const { job, columns, filter, caseVariable, filteredCases, totalCases } = opts
  const lines: string[] = [`I want to analyse respondent-level data from the job ${job}.`, '', 'Variables, in this order:']
  for (const col of columns) {
    const note = TYPE_NOTES[col.data_type]
    lines.push(`- ${col.name}${col.description ? `: ${col.description}` : ''}${note ? ` (${note})` : ''}`)
  }
  lines.push('')

  const expression = filter.trim()
  lines.push(expression ? `Filter: only respondents matching ${expression}.` : 'No filter: every respondent.')
  if (filteredCases !== undefined && totalCases !== undefined) {
    lines.push(`That is ${filteredCases.toLocaleString('en-GB')} of ${totalCases.toLocaleString('en-GB')} respondents.`)
  }
  lines.push('')

  const names = columns.map((c) => c.name)
  if (caseVariable && !names.includes(caseVariable)) names.unshift(caseVariable)
  lines.push(`Read the rows with: pt casedata ${names.join(' ')}${expression ? ` --filter "${expression}"` : ''}`)
  lines.push(
    caseVariable
      ? `Each row is one respondent, in data order. ${caseVariable} is the case id.`
      : 'Each row is one respondent, in data order; this job has no case id variable, so refer to a respondent by its position (1 is the first).',
  )
  lines.push('', 'My question: ')
  return lines.join('\n')
}
